import { Box, Container, Grid, styled } from "@mui/material";
import {
  createSearchParams,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  getTradeAmountMax,
  getTradeInsertForm,
} from "store/slices/trade/tradeSlice";
import { useEffect, useState } from "react";

import BasicTradeFormWrapper from "./BasicTradeFormWrapper";
import MarketDrawer from "./components/MarketDrawer";
import TradeFormSkeleton from "components/Skeletons/TradeForm.skeleton";
import TradeHead from "./components/TradeHead";
import TradeHistory from "./components/TradeHistory";
import TradingViewChart from "./components/TradeChartView/Components/TradingViewChart";
import { getInvestmentMarketWithAccountBroker } from "store/slices/market/marketSlice";
import { repeat } from "utils/helpers/interval";
import useDispatch from "hooks/useDispatch";
import useIsMobileView from "hooks/useIsMobileView";
import { useSelector } from "react-redux";
import { getBrokerInfo } from "store/slices/wallet/walletSlice";

let getTradeAmountMaxInterval;
let getInvestmentMarketInterval;

export function BasicTrade() {
  const isMobileView = useIsMobileView();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { source, destination } = useParams();

  const tradeInsertForm = useSelector(
    (state) => state.trade?.tradeInsertForm?.data
  );

  const coinMarketDetails = useSelector(
    (state) => state.market.investmentMarket?.data
  );

  const [loading, setLoading] = useState(true);
  const [showMarketDrawer, setShowMarketDrawer] = useState(false);

  const [searchParams] = useSearchParams();
  const allSearchParams = Object.fromEntries([...searchParams]);

  const basketId = +searchParams.get("basketId") || tradeInsertForm?.basketId || 0;
  const brokerId = +searchParams.get("brokerId") || tradeInsertForm?.brokerId || 0;
  const sideId = +searchParams.get("sideId") || 1;
  const borrow = !!searchParams.get("borrow");
  const repay = !!searchParams.get("repay");

  const handleCloseMarketDrawer = () => {
    setShowMarketDrawer(false);
  };

  const handleGetTradeInsertForm = async () => {
    setLoading(true);

    await dispatch(
      getTradeInsertForm({
        basketId: basketId,
        coinDestinationId: destination,
        sideId,
        coinSourceId: source,
      })
    );
    setLoading(false);
  };

  useEffect(() => {
    handleGetTradeInsertForm();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (brokerId) {
      dispatch(getBrokerInfo({ brokerId }))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brokerId]);

  useEffect(() => {
    dispatch(
      getInvestmentMarketWithAccountBroker({
        brokerId,
        coinSourceId: source,
        coinDestinationId: destination,
      })
    );

    clearInterval(getInvestmentMarketInterval);

    getInvestmentMarketInterval = repeat({
      action: () =>
        dispatch(
          getInvestmentMarketWithAccountBroker({
            brokerId,
            coinSourceId: source,
            coinDestinationId: destination,
          })
        ),
      time: 1000,
    });

    return () => {
      clearInterval(getInvestmentMarketInterval);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, destination, brokerId, basketId]);
  // ----------------- END TRADE INITIAL ----------------------- //

  useEffect(() => {
    if (tradeInsertForm?.validSeconds) {
      dispatch(
        getTradeAmountMax({
          basketId,
          sideId,
          coinDestinationId: destination,
          coinSourceId: source,
          brokerId,
        })
      );

      clearInterval(getTradeAmountMaxInterval);

      getTradeAmountMaxInterval = repeat({
        action: () =>
          getTradeAmountMax({
            basketId,
            sideId,
            coinDestinationId: destination,
            coinSourceId: source,
            brokerId,
          }),
        time: tradeInsertForm?.validSeconds * 1000,
      });
    }

    return () => {
      clearInterval(getTradeAmountMaxInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tradeInsertForm?.validSeconds,
    destination,
    brokerId,
    basketId,
    source,
    borrow,
    repay,
    sideId,
  ]);

  return (
    <>
      <Container maxWidth="100%">
        <Grid container spacing={1}>
          {!isMobileView && (
            <Grid item xs={12} md={7} lg={8}>
              <TradingViewHeader>
                {!loading && (
                  <TradeHead
                    setShowMarketDrawer={setShowMarketDrawer}
                    coinMarketDetails={coinMarketDetails}
                  />
                )}
              </TradingViewHeader>

              <TradingViewChart
                height="432"
                symbol={coinMarketDetails?.coinSourceName}
              />
            </Grid>
          )}

          {!loading ? (
            <Grid item xs={12} md={5} lg={4}>
              <BasicTradeFormWrapper
                setShowMarketDrawer={setShowMarketDrawer}
                coinMarketDetails={coinMarketDetails}
              />
            </Grid>
          ) : (
            <Grid item xs={12} md={5} lg={4}>
              <TradeFormSkeleton />
            </Grid>
          )}
        </Grid>

        <Grid container id="trade_history">
          <Grid item xs={12} mb={5}>
            <TradeHistory />
          </Grid>
        </Grid>

        {!loading && (
          <MarketDrawer
            onRowClick={(row) => {
              navigate({
                pathname: `/trade/${row.data.coinSourceId}/${row.data.coinDestinationId}`,
                search: `?${createSearchParams(allSearchParams)}`,
              });

              handleCloseMarketDrawer();
            }}
            open={showMarketDrawer}
            onClose={handleCloseMarketDrawer}
          />
        )}
      </Container>
    </>
  );
}

export const TradingViewHeader = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.alternate,
  borderTopRightRadius: "8px",
  borderTopLeftRadius: "8px",
}));

export const HelpContextWrapper = styled(Box)(({ theme }) => ({
  ".blue-text": {
    color: theme.palette.text.alternate,
  },

  ".red-text": {
    color: theme.palette.error.main,
  },

  "n-weight-500": {
    fontWeight: 500,
  },

  "n-weight-600": {
    fontWeight: 600,
  },

  "n-weight-700": {
    fontWeight: 700,
  },
}));

export default BasicTrade;
